import { Meta, Title } from "@solidjs/meta";
import { query, useLocation } from "@solidjs/router";
import { getBackendURL } from "@storytell/client/src/getBaseUrl";
import type { Component } from "solid-js";
import { createResource, Suspense } from "solid-js";
import { getAppBaseUrl } from "~/lib/getAppBaseUrl";
import { getSocialCardImageUrl } from "~/lib/socialCard";

interface MetaData {
  title: string;
  description: string;
}

/**
 * Cached fetch for shared live-session metadata.
 * Using SolidStart's cache() ensures proper SSR data loading.
 */
const getSharedLiveSessionMeta = query(async (token: string): Promise<MetaData | null> => {
  "use server";
  try {
    const baseUrl = getBackendURL();
    const url = `${baseUrl}/public-live-sessions/${token}/meta`;
    const response = await fetch(url);
    if (!response.ok) return null;
    return (await response.json()) as MetaData;
  } catch {
    return null;
  }
}, "shared-live-session-meta");

const extractTokenFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/shared\/live-session\/([^/]+)$/);
  return match ? match[1] : null;
};

/**
 * SharedLiveSessionSSRMeta - Renders meta tags for shared live-session routes
 * during SSR, so link unfurls (Open Graph / Twitter cards) see them. Must be
 * rendered outside ClientSideSplashScreen, alongside SharedChatSSRMeta.
 */
export const SharedLiveSessionSSRMeta: Component = () => {
  const location = useLocation();
  const token = extractTokenFromPath(location.pathname);

  if (!token) return null;

  const url = `${getAppBaseUrl()}/shared/live-session/${token}`;

  return (
    <Suspense>
      <SharedLiveSessionMetaFetcher token={token} url={url} />
    </Suspense>
  );
};

const imageUrl = () => getSocialCardImageUrl(getAppBaseUrl());

const SharedLiveSessionMetaFetcher: Component<{ token: string; url: string }> = (props) => {
  const [meta] = createResource(() => getSharedLiveSessionMeta(props.token), { deferStream: true });

  const title = () => meta()?.title || "Shared Live Session";
  const fullTitle = () => `${title()} | Chief`;
  const description = () => meta()?.description || "A live session on Chief.";

  return (
    <>
      <Title>{fullTitle()}</Title>
      <Meta property="og:title" content={title()} />
      <Meta property="og:description" content={description()} />
      <Meta property="og:type" content="article" />
      <Meta property="og:url" content={props.url} />
      <Meta property="og:site_name" content="Chief" />
      <Meta property="og:image" content={imageUrl()} />
      <Meta name="twitter:card" content="summary_large_image" />
      <Meta name="twitter:title" content={title()} />
      <Meta name="twitter:description" content={description()} />
      <Meta name="twitter:image" content={imageUrl()} />
    </>
  );
};
